// noinspection SpellCheckingInspection

import React from 'react';
import PropTypes from 'prop-types';
import { Col, Div, Icon, Modal, Row, Text } from '@pinpoint/design-kit';
import Button from 'src/components/button';
import { DialogActionProp } from 'src/store/typings';

function BaseDialog({
  isOpen,
  actions: {
    close: { before: beforeClose = () => {}, on: onClose = () => {}, after: afterClose = () => {} },
    cancel: { before: beforeCancel = () => {}, on: onCancel = () => {}, after: afterCancel = () => {} },
    handlers = [],
  },
  modalWidth = '40%',
  allowClickOff = true,
  hideCloseIcon = false,
  title: {
    text: titleText = 'User Action is Required',
    textSize: titleSize = 'subheader',
    backgroundColor: titleBgColor = 'white',
    color: titleTextColor = 'black',
    icon: titleIcon = <Div />,
  } = {
    text: 'User Action is Required',
    textSize: 'subheader',
    backgroundColor: 'white',
    color: 'black',
    icon: <Div />,
  },
  items: { content = [], buttons = [] },
}) {
  const handleCancel = async () => {
    if (beforeCancel) await beforeCancel();
    await onCancel();
    if (afterCancel) await afterCancel();
  };

  const handleClose = async () => {
    if (beforeClose) await beforeClose();
    await onClose();
    if (afterClose) await afterClose();
  };

  const handleClickoff = async () => {
    if (allowClickOff) {
      await handleClose();
    }
  };

  return (
    <Div data-test="modal-wrapper">
      <Modal
        maxW="90%"
        maxH="calc(100vh - 64px)"
        minW="607px"
        overflow={content.length > 10 ? 'hidden' : undefined}
        w={modalWidth}
        align="center"
        rounded="md"
        isOpen={isOpen}
        onClose={handleClickoff}
        data-test="modal-container"
      >
        <Div
          d="flex"
          data-test="content-title"
          rounded={{ tr: 'md', tl: 'md' }}
          bg={titleBgColor}
          p={{ t: '1.625rem', b: '1.375rem', x: '2.875rem' }}
          m={{ x: '-2rem', t: '-2rem' }}
          textColor={titleTextColor}
          justify="space-between"
          align="center"
        >
          {!hideCloseIcon && (
            <Icon
              name="Cross"
              pos="absolute"
              top="1rem"
              right="1rem"
              size="1rem"
              onClick={handleClose}
              cursor="pointer"
              color={titleTextColor}
            />
          )}
          <Text textSize={titleSize}>{titleText}</Text>
          {titleIcon}
        </Div>
        <Row data-test="content-body" m={{ t: '2.5rem' }}>
          <Col m="0" size="12" data-test="content-col" gutterWidth="0" maxH="100%" h="100%">
            <Row
              d="flow-root"
              data-test="scrollable-row"
              align="space-between"
              overflow={content.length > 10 ? 'auto' : undefined}
              maxH={`calc(90vh - ${content.length > 5 ? 200 : content.length * 50}px)`}
            >
              <div data-test="item" />
              {content.map((elem) => (
                <Col>{elem}</Col>
              ))}
            </Row>
            <Row data-test="button-row">
              <Col data-test="button-col" alignSelf="flex-end" justify="space-between" flexGrow="1">
                <Div d="flex" flexDir="row-reverse" justify="flex-start" data-test="button-div" m={{ t: '1.25rem' }}>
                  {buttons.map((elem, idx) => (
                    <Button
                      buttonStyle="primary"
                      m={{ l: '0.5rem', r: '0rem' }}
                      key={elem.text}
                      disabled={elem.disabled}
                      onClick={async () => {
                        if (handlers[idx].before) await handlers[idx].before();
                        await handlers[idx].on();
                        if (handlers[idx].after) await handlers[idx].after();
                        if (elem.close) {
                          await handleClose();
                        }
                      }}
                    >
                      {elem.icon ? elem.icon : null}
                      <Text textSize={elem.textSize}>{elem.text}</Text>
                    </Button>
                  ))}
                  <Button
                    buttonStyle="secondary"
                    m={{ r: '0rem' }}
                    onClick={async () => {
                      await handleCancel();
                      await handleClose();
                    }}
                  >
                    CANCEL
                  </Button>
                </Div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </Div>
  );
}

/* eslint-disable react/require-default-props */
BaseDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalWidth: PropTypes.string,
  allowClickOff: PropTypes.bool,
  hideCloseIcon: PropTypes.bool,
  actions: DialogActionProp.isRequired,
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
    icon: PropTypes.element,
  }),
  items: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.element).isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.element,
        text: PropTypes.string.isRequired,
        textSize: PropTypes.string,
        close: PropTypes.bool.isRequired,
      }).isRequired
    ),
  }).isRequired,
};
/* eslint-enable */

export default BaseDialog;
